import { Component, EventEmitter, HostListener, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IActivityLog, IMobileWhitelistChangeRecord } from 'app/models/activity-log.model';
import { IdentityFormatPipe, MomentPipe } from 'app/pipes/pipes';
import { AnchorWrapPipe, EscapeHtmlPipe, StrongWrapPipe } from '../activity-log-table/activity-log-table-pipes';

/**
 * Activity details of an EMA phone whitelist change (request, approve, deny, block or modify):
 * the phone name and GSM number, and the old and new name and number when they changed. The mobile ID is
 * not shown: it is an internal EMA identifier the Portal does not show anywhere else either.
 */
@Component({
  selector: 'app-activity-mobile-whitelist-details',
  imports: [CommonModule, TranslateModule, RouterModule, IdentityFormatPipe, MomentPipe, AnchorWrapPipe, StrongWrapPipe, EscapeHtmlPipe],
  templateUrl: './activity-mobile-whitelist-details.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  styleUrls: ['../activity-details-dialog/activity-details-content-styles.css']
})
export class ActivityMobileWhitelistDetailsComponent implements OnChanges {
  @Output() closeDialog = new EventEmitter<void>();
  @Input() log: IActivityLog;

  changes: IMobileWhitelistChangeRecord | null = null;
  nameChanged = false;
  numberChanged = false;

  constructor(private router: Router) {}

  /** The staff link in the translated "by user" text is plain HTML, so route its clicks here */
  @HostListener('document:click', ['$event'])
  onLinkClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement).closest('a.link');
    if (!anchor) return;
    event.preventDefault();
    const url = anchor.getAttribute('data-url');
    if (url) this.router.navigateByUrl(url);
    this.closeDialog.emit();
  }

  ngOnChanges(): void {
    this.changes = this.log?.details?.mobileWhitelistChanges ?? null;
    // The API only sends old and new values for the fields that changed
    this.nameChanged = this.changes?.oldName !== undefined || this.changes?.newName !== undefined;
    this.numberChanged = this.changes?.oldGsmNumber !== undefined || this.changes?.newGsmNumber !== undefined;
  }

  /** A new whitelisting request: every field is new */
  get created(): boolean {
    return this.log?.action === 'Create';
  }

  /** Denied and blocked phones are gone from the whitelist */
  get removed(): boolean {
    return this.log?.action === 'Delete';
  }

  get phoneName(): string | undefined {
    return this.changes?.name || this.log?.details?.targetName || undefined;
  }
}
